// packages/frontend/src/features/shelves/SaveShelfDialog.tsx
//
// "Save as shelf" (Plan 13, Task 10): turns the library's *current* filter
// set (taxonomy chips + time cap) into a custom shelf. Membership stays
// fully derived — this only appends a `{ id, name, filter }` definition to
// the household's shelf-config document (read-modify-write against the
// freshest `useShelfConfig` snapshot, same idiom as ManageShelvesSheet); no
// per-recipe filing happens here or anywhere else in the app.
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { newId, type ShelfFilter } from "@recipevault/shared";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog.js";
import { friendlyError } from "../../shared/http.js";
import { sync } from "../../sync/SyncService.js";
import { useShelfConfig } from "./useShelfConfig.js";
import type { LibraryFilters } from "../recipes/useRecipes.js";

export interface SaveShelfDialogProps {
  filters: LibraryFilters;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const NAME_MAX = 60;

/**
 * Maps the library's LibraryFilters (chip-bar shape) onto a ShelfFilter (the
 * shared, persisted shape) — only fields that are actually set are written.
 * deriveShelves' `toLibraryFilters` reads a missing taxonomy array the same
 * as an empty one (`filter.tagIds ?? []`, etc.), so omitting an empty array
 * here round-trips to the exact same membership the flat view currently
 * shows — no double-application, no drift.
 *
 * `cuisineIds` is plural in LibraryFilters (multi-select) but ShelfFilter
 * only supports a single `cuisineId` by design — the first selection wins
 * when more than one cuisine chip happens to be active.
 */
export function toShelfFilter(filters: LibraryFilters): ShelfFilter {
  const filter: ShelfFilter = {};
  if (filters.tagIds.length > 0) filter.tagIds = filters.tagIds;
  if (filters.categoryIds.length > 0) filter.categoryIds = filters.categoryIds;
  if (filters.dietFlags.length > 0) filter.dietFlags = filters.dietFlags;
  if (filters.cuisineIds.length > 0) filter.cuisineId = filters.cuisineIds[0];
  if (filters.maxTotalTimeMin != null) filter.maxTotalTimeMin = filters.maxTotalTimeMin;
  return filter;
}

export function SaveShelfDialog({ filters, open, onOpenChange }: SaveShelfDialogProps): JSX.Element {
  const { config, loading } = useShelfConfig();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset the form every time the dialog opens — a stale name/error from a
  // previous save-as-shelf must never leak into the next one.
  useEffect(() => {
    if (open) {
      setName("");
      setErr(null);
    }
  }, [open]);

  const trimmed = name.trim();
  const valid = trimmed.length > 0 && trimmed.length <= NAME_MAX;

  async function handleSave(): Promise<void> {
    if (!config || loading || !valid) return;
    setSaving(true);
    setErr(null);
    try {
      // Duplicate names are allowed — the id (not the name) is the identity,
      // so saving twice under the same name just yields two distinct shelves.
      const entry = { id: newId("shelf"), name: trimmed, filter: toShelfFilter(filters) };
      await sync.putShelfConfigOptimistic({
        schemaVersion: config.schemaVersion,
        order: [...config.order, entry.id],
        hidden: config.hidden,
        custom: [...config.custom, entry],
      });
      toast.success(`Saved "${trimmed}" as a shelf`);
      onOpenChange(false);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save as shelf</DialogTitle>
          <DialogDescription>
            Adds a shelf to your library home with these filters. Membership updates automatically
            as recipes change — nothing gets filed.
          </DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          autoComplete="off"
          placeholder="e.g. Quick weeknight dinners"
          value={name}
          maxLength={NAME_MAX}
          onChange={(e) => { setName(e.target.value); }}
          aria-label="Shelf name"
        />
        {err && <p className="text-sm text-destructive">{err}</p>}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="min-h-12"
            onClick={() => { onOpenChange(false); }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="min-h-12"
            onClick={() => { void handleSave(); }}
            disabled={!valid || saving || loading}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
